import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, X } from "lucide-react";

/**
 * StoryPrompt - a discreet bottom-right reading card that surfaces after
 * one minute on the home page. Designed to feel like a hand-typed memo
 * slipped onto the reader's desk, not a popup. Dismisses for the session.
 *
 * UX:
 *  - Slides up from bottom-right (bottom-center on mobile)
 *  - Brass top rule with two diamonds, folio mark, display serif headline,
 *    italic standfirst, mono CTA
 *  - Escape key closes it
 *  - Hidden on /our-story, /admin, and once the user has dismissed it once
 *    in the session
 */

const PROMPT_DELAY_MS = 60000;
const DISMISSED_KEY = "vilora-story-prompt-dismissed";
const HIDDEN_PATHS = new Set(["/admin", "/our-story"]);

export default function StoryPrompt() {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const cardRef = useRef(null);
  const closeBtnRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (HIDDEN_PATHS.has(location.pathname)) {
      setVisible(false);
      return undefined;
    }
    if (window.sessionStorage.getItem(DISMISSED_KEY) === "1") {
      return undefined;
    }
    const timer = window.setTimeout(() => setVisible(true), PROMPT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  // Move focus to the close button when the card opens - accessible without
  // trapping focus (it's a non-blocking notification, not a modal).
  useEffect(() => {
    if (visible) {
      const id = window.setTimeout(() => closeBtnRef.current?.focus(), 80);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [visible]);

  // Close on Escape
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const dismiss = () => {
    window.sessionStorage.setItem(DISMISSED_KEY, "1");
    setClosing(true);
    window.setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 280);
  };

  const openStory = () => {
    window.sessionStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
    navigate("/our-story");
  };

  if (!visible) return null;

  return (
    <aside
      ref={cardRef}
      className={`ledger story-prompt ${closing ? "is-closing" : ""}`}
      role="complementary"
      aria-live="polite"
      aria-label="A note about our story"
    >
      <div className="story-prompt-rule" aria-hidden="true" />

      <div className="story-prompt-head">
        <span className="story-prompt-folio">
          <span className="story-prompt-dot" aria-hidden="true" />
          <span>FOLIO · APPENDIX</span>
        </span>
        <button
          ref={closeBtnRef}
          type="button"
          className="story-prompt-close"
          aria-label="Dismiss"
          onClick={dismiss}
        >
          <X size={14} strokeWidth={1.6} />
        </button>
      </div>

      <h2 className="story-prompt-title">
        There is a <em>longer</em>
        <br />
        story behind this house.
      </h2>

      <p className="story-prompt-deck">
        <em>Four chapters.</em> One family. From a counter in Rajkot to a
        container at Mundra - and the people who carry the ledger between.
      </p>

      <div className="story-prompt-arc" aria-hidden="true">
        <span>Counter</span>
        <span className="story-prompt-arrow">→</span>
        <span>Warehouse</span>
        <span className="story-prompt-arrow">→</span>
        <span>Mill</span>
        <span className="story-prompt-arrow">→</span>
        <span>Port</span>
      </div>

      <button
        type="button"
        className="story-prompt-cta"
        onClick={openStory}
      >
        <span className="story-prompt-cta-num">№ 02·B</span>
        <span className="story-prompt-cta-label">Read the story</span>
        <span className="story-prompt-cta-arrow">
          <ArrowUpRight size={14} strokeWidth={1.7} />
        </span>
      </button>

      <span className="story-prompt-corner story-prompt-corner--tl" aria-hidden="true" />
      <span className="story-prompt-corner story-prompt-corner--br" aria-hidden="true" />
    </aside>
  );
}
